import { defineStore } from "pinia";
import { GetCashierProfiles, GetPosProfileDetail } from "@/api/posProfileApi";
import { canPage, hasAnyPermission } from "@/helper/permissionHelper";
import { PAGE_KEYS } from "@/config/moduleRegistry/keys";
import { DEFAULT_MODE_OF_PAYMENT } from "@/services/paymentMode";
import { useAuthStore } from "@/stores/auth.store";
import { Permissions } from "@/type/api";
import type { ActiveCashierContext, PosProfileAssignedUser, PosProfileDetail } from "@/type/posProfile";

// The Cashiers page is backed by the `POS Profile` doctype. Users without that
// page (e.g. Coordinator / Clinical Services) have no `POS Profile` read
// permission, so listing profiles would 403. Gate the network read on it.
//
// ⚠️ POS COUNTS TOO, AND LEAVING IT OUT LOCKED CASHIERS OUT OF THE TILL.
// The Cashiers page is `ACCOUNTING_ROLES` (Accounts User/Manager) while POS is
// `SALES_ACCOUNTING_ROLES`, which also includes Sales User and Sales Manager. A
// Sales User could therefore open POS while this gate refused to load any
// profile — so the navbar selector never rendered, no cashier could be chosen,
// and checkout stayed permanently disabled with nothing explaining why.
//
// Operating a till and administering till configuration are different rights.
// Coordinator and clinical roles hold neither key, so the 403 this guard exists
// to prevent is still prevented.
function canLoadCashierProfiles() {
  return canPage(PAGE_KEYS.ACCOUNTING_CASHIERS) || canPage(PAGE_KEYS.POS_SELL);
}

const CASHIER_STORAGE_KEY = "alkokh_active_cashier_profile";

function readStoredCashierContext(): ActiveCashierContext | null {
  if (typeof window === "undefined")
    return null;

  try {
    const raw = window.localStorage.getItem(CASHIER_STORAGE_KEY);
    if (!raw)
      return null;

    const parsed = JSON.parse(raw) as ActiveCashierContext | null;
    if (!parsed?.posProfileName)
      return null;

    return {
      posProfileId: String(parsed.posProfileId || parsed.posProfileName),
      posProfileName: String(parsed.posProfileName),
      company: parsed.company || null,
      warehouse: parsed.warehouse || null,
      cashAccount: parsed.cashAccount || null,
      sellingPriceList: parsed.sellingPriceList || null,
      priceListCurrency: parsed.priceListCurrency || null,
      currency: parsed.currency || null,
      defaultCustomer: parsed.defaultCustomer || null,
      defaultModeOfPayment: parsed.defaultModeOfPayment || null,
      allowedModeOfPayments: Array.isArray(parsed.allowedModeOfPayments)
        ? parsed.allowedModeOfPayments.map(value => String(value)).filter(Boolean)
        : [],
      assignedUsers: Array.isArray(parsed.assignedUsers)
        ? parsed.assignedUsers
            .map((user) => {
              const id = String(user?.id ?? "").trim();
              const label = String(user?.label ?? user?.id ?? "").trim() || id;
              return id ? { id, label } : null;
            })
            .filter((user): user is PosProfileAssignedUser => Boolean(user))
        : [],
    };
  } catch {
    return null;
  }
}

function persistCashierContext(context: ActiveCashierContext | null) {
  if (typeof window === "undefined")
    return;

  if (!context) {
    window.localStorage.removeItem(CASHIER_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(CASHIER_STORAGE_KEY, JSON.stringify(context));
}

function toActiveCashierContext(profile: PosProfileDetail): ActiveCashierContext {
  return {
    posProfileId: profile.id,
    posProfileName: profile.name,
    company: profile.company,
    warehouse: profile.warehouse,
    cashAccount: profile.cashAccount || null,
    sellingPriceList: profile.sellingPriceList || null,
    priceListCurrency: profile.priceListCurrency || null,
    currency: profile.currency || null,
    defaultCustomer: profile.defaultCustomer || null,
    defaultModeOfPayment: profile.defaultModeOfPayment,
    allowedModeOfPayments: profile.allowedModeOfPayments,
    assignedUsers: profile.assignedUsers,
  };
}

function normalizeIdentity(value: unknown) {
  return String(value ?? "").trim().toLowerCase();
}

function getCurrentUserIdentifiers() {
  const auth = useAuthStore();
  const identifiers = [
    auth.username,
    auth.user?.name,
    auth.user?.email,
    auth.user?.username,
  ]
    .map(normalizeIdentity)
    .filter(Boolean);

  return [...new Set(identifiers)];
}

function isCashierAdmin() {
  return hasAnyPermission([
    Permissions.Administrator,
    Permissions.SystemManager,
    Permissions.PetAppAdmin,
  ]);
}

function isProfileAssignedToCurrentUser(assignedUsers: PosProfileAssignedUser[]) {
  if (isCashierAdmin())
    return true;

  if (!assignedUsers.length)
    return false;

  const currentUserIds = getCurrentUserIdentifiers();
  if (!currentUserIds.length)
    return false;

  return assignedUsers.some(user =>
    currentUserIds.includes(normalizeIdentity(user.id)),
  );
}

export const useCashierStore = defineStore("cashier", {
  state: () => {
    const stored = readStoredCashierContext();

    return {
      activeProfileId: stored?.posProfileId ?? null,
      activeProfileName: stored?.posProfileName ?? null,
      company: stored?.company ?? null,
      warehouse: stored?.warehouse ?? null,
      cashAccount: stored?.cashAccount ?? null,
      sellingPriceList: stored?.sellingPriceList ?? null,
      priceListCurrency: stored?.priceListCurrency ?? null,
      currency: stored?.currency ?? null,
      defaultCustomer: stored?.defaultCustomer ?? null,
      defaultModeOfPayment: stored?.defaultModeOfPayment ?? null,
      allowedModeOfPayments: stored?.allowedModeOfPayments ?? [],
      assignedUsers: stored?.assignedUsers ?? [],
      availableProfiles: [] as PosProfileDetail[],
      loading: false,
      loaded: false,
      error: null as string | null,
    };
  },

  getters: {
    hasActiveProfile: state => Boolean(state.activeProfileName),
    canManageAllProfiles: () => isCashierAdmin(),
    canUseActiveProfile: state => {
      if (!state.activeProfileName)
        return false;

      return isProfileAssignedToCurrentUser(state.assignedUsers);
    },
    preferredModeOfPayment: (state) => {
      return state.defaultModeOfPayment
        || state.allowedModeOfPayments[0]
        || DEFAULT_MODE_OF_PAYMENT;
    },
    viewableProfiles: (state) => {
      if (isCashierAdmin())
        return state.availableProfiles;

      return state.availableProfiles.filter(profile =>
        isProfileAssignedToCurrentUser(profile.assignedUsers),
      );
    },
    shouldShowSelector(): boolean {
      return this.canManageAllProfiles
        || this.hasActiveProfile
        || this.viewableProfiles.length > 0;
    },
    activeProfile: (state): ActiveCashierContext | null => {
      if (!state.activeProfileName)
        return null;

      return {
        posProfileId: state.activeProfileId || state.activeProfileName,
        posProfileName: state.activeProfileName,
        company: state.company,
        warehouse: state.warehouse,
        cashAccount: state.cashAccount,
        sellingPriceList: state.sellingPriceList,
        priceListCurrency: state.priceListCurrency,
        currency: state.currency,
        defaultCustomer: state.defaultCustomer,
        defaultModeOfPayment: state.defaultModeOfPayment,
        allowedModeOfPayments: state.allowedModeOfPayments,
        assignedUsers: state.assignedUsers,
      };
    },
  },

  actions: {
    loadFromStorage() {
      const stored = readStoredCashierContext();

      this.activeProfileId = stored?.posProfileId ?? null;
      this.activeProfileName = stored?.posProfileName ?? null;
      this.company = stored?.company ?? null;
      this.warehouse = stored?.warehouse ?? null;
      this.cashAccount = stored?.cashAccount ?? null;
      this.sellingPriceList = stored?.sellingPriceList ?? null;
      this.priceListCurrency = stored?.priceListCurrency ?? null;
      this.currency = stored?.currency ?? null;
      this.defaultCustomer = stored?.defaultCustomer ?? null;
      this.defaultModeOfPayment = stored?.defaultModeOfPayment ?? null;
      this.allowedModeOfPayments = stored?.allowedModeOfPayments ?? [];
      this.assignedUsers = stored?.assignedUsers ?? [];
    },

    clearActiveProfile() {
      this.activeProfileId = null;
      this.activeProfileName = null;
      this.company = null;
      this.warehouse = null;
      this.cashAccount = null;
      this.sellingPriceList = null;
      this.priceListCurrency = null;
      this.currency = null;
      this.defaultCustomer = null;
      this.defaultModeOfPayment = null;
      this.allowedModeOfPayments = [];
      this.assignedUsers = [];
      persistCashierContext(null);
    },

    setActiveProfile(profile: PosProfileDetail) {
      const context = toActiveCashierContext(profile);

      this.activeProfileId = context.posProfileId;
      this.activeProfileName = context.posProfileName;
      this.company = context.company;
      this.warehouse = context.warehouse;
      this.cashAccount = context.cashAccount ?? null;
      this.sellingPriceList = context.sellingPriceList ?? null;
      this.priceListCurrency = context.priceListCurrency ?? null;
      this.currency = context.currency ?? null;
      this.defaultCustomer = context.defaultCustomer ?? null;
      this.defaultModeOfPayment = context.defaultModeOfPayment;
      this.allowedModeOfPayments = context.allowedModeOfPayments;
      this.assignedUsers = context.assignedUsers;
      persistCashierContext(context);
    },

    canAccessProfile(profile: { assignedUsers: PosProfileAssignedUser[] }) {
      return isProfileAssignedToCurrentUser(profile.assignedUsers);
    },

    /**
     * Put the till on a profile when nothing is selected.
     *
     * A cashier who opens POS with no active profile gets a warning panel and a dead
     * terminal, and the fix is always the same: go to Cashiers, pick the one they are
     * assigned to, come back. When there IS one to pick, the app can do that itself.
     *
     * ⚠️ `viewableProfiles`, NEVER `availableProfiles`. The second is every profile the
     * read returned; the first is the ones this user is actually assigned to (and is
     * all of them for an admin). Auto-selecting an unassigned profile would set a
     * warehouse and a cash account the user has no business posting to, and
     * `refreshActiveProfile` would tear it back down on the next load anyway.
     *
     * ⚠️ IT ONLY EVER FILLS A VOID. It does not re-pick over a live selection, so a
     * cashier who chose their till keeps it. The choice is persisted, so this fires
     * once per device rather than on every visit.
     *
     * ⚠️ WITH SEVERAL PROFILES THIS IS A GUESS, and the guess has consequences — the
     * profile carries the warehouse, the cash account, the price list and the
     * currency. It is the right trade only because the alternative is a terminal that
     * does nothing, and because the cashier can still switch from the header. If a
     * site needs the first profile to be deterministic, order it in the backend list
     * rather than adding a tiebreak here.
     */
    autoSelectFirstProfile() {
      if (this.activeProfileName)
        return false;

      const first = this.viewableProfiles[0];
      if (!first)
        return false;

      this.setActiveProfile(first);

      return true;
    },

    async refreshActiveProfile() {
      if (!this.activeProfileName)
        return null;

      const detail = await GetPosProfileDetail(this.activeProfileName);
      if (!detail)
        return null;

      if (!this.canAccessProfile(detail)) {
        this.clearActiveProfile();
        this.error = "This cashier is not assigned to the current user.";
        return null;
      }

      this.setActiveProfile(detail);
      return detail;
    },

    async activateProfile(profileName: string) {
      const name = String(profileName ?? "").trim();
      if (!name)
        return null;

      this.loading = true;
      this.error = null;

      try {
        const detail = await GetPosProfileDetail(name);
        if (!detail)
          return null;

        if (!this.canAccessProfile(detail)) {
          this.error = "This cashier is not assigned to the current user.";
          return null;
        }

        this.setActiveProfile(detail);
        return detail;
      } catch (error: any) {
        this.error = error?.message || "Failed to load cashier.";
        return null;
      } finally {
        this.loading = false;
      }
    },

    async loadProfiles() {
      if (this.loading)
        return;

      // Skip the `POS Profile` read entirely for users without cashier access
      // (prevents a 403 on the coordinator/clinical pages where the layout
      // mounts this store unconditionally). Mark as loaded so callers settle.
      if (!canLoadCashierProfiles()) {
        this.availableProfiles = [];
        this.loaded = true;

        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const profiles = await GetCashierProfiles();
        this.availableProfiles = profiles;
        this.loaded = true;

        if (this.activeProfileName) {
          const activeProfile = profiles.find(profile => profile.id === this.activeProfileName);

          /**
           * ⚠️ AN EMPTY LIST IS NOT EVIDENCE THAT ACCESS WAS LOST.
           *
           * `GetCashierProfiles` swallows its own failures and returns `[]`, so
           * offline — or after any failed read — this branch used to conclude the
           * user had been unassigned and DELETE the stored till. Measured in the
           * Electron build: reload with no network and the till reopened with
           * "Cashier is required for POS checkout", unable to sell, on a device
           * holding unsent sales. The same class of bug docs/AGENT.md already
           * names: a failed read must not clear what it failed to verify.
           *
           * A non-empty list that does not contain the active profile IS evidence
           * (renamed, unassigned, disabled), and still clears.
           */
          if (!profiles.length) {
            console.warn("Cashier profiles could not be listed; keeping the stored till rather than treating an empty answer as lost access.");
          }
          else if (!activeProfile || !this.canAccessProfile(activeProfile)) {
            this.clearActiveProfile();
          }
          else {
            this.setActiveProfile(activeProfile);
          }
        }

        // ⚠️ AFTER the reconcile above, not instead of it. This also catches the case
        // where the stored choice was just cleared because the user lost access to it:
        // moving them to a till they DO hold beats leaving the terminal dead.
        this.autoSelectFirstProfile();
      } catch (error: any) {
        this.error = error?.message || "Failed to load cashiers.";
      } finally {
        this.loading = false;
      }
    },

    async ensureReady() {
      if (!this.loaded && !this.loading)
        await this.loadProfiles();

      // ⚠️ The store is mounted by the layout, so the list is often already loaded
      // from another page and `loadProfiles` short-circuits above — which would skip
      // the auto-select inside it. Cheap and idempotent: it no-ops the moment a
      // profile is active.
      this.autoSelectFirstProfile();

      // ⚠️ `defaultCustomer` IS INTENTIONALLY NOT IN THIS LIST. A blank Default Customer
      // on the POS Profile is a legitimate configuration, so treating it as "stale" would
      // re-read the profile on every single `ensureReady()` for every till that leaves it
      // unset. Contexts cached before the field existed pick it up on the next refresh
      // any of the conditions below already triggers.
      const needsActiveProfileRefresh = !this.allowedModeOfPayments.length
        || !this.assignedUsers.length
        || !this.cashAccount
        || !this.sellingPriceList
        || !this.priceListCurrency;

      if (this.activeProfileName && needsActiveProfileRefresh)
        await this.refreshActiveProfile();
    },
  },
});
